/**
 * Convert To Client Modal
 * Converte o lead em cliente (pessoa física ou jurídica).
 */

import React, { useState } from 'react';
import { Building2, Loader2, User } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/utils/cn';
import type { Lead } from '../../types/leads';
import { leadsService } from '../../services/leads-service';
import { getErrorMessage } from '../../utils/error.utils';

interface ConvertToClientModalProps {
  lead: Lead;
  onClose: () => void;
  onSuccess: () => void;
}

const CLIENT_TYPES = [
  { value: 'PERSON', label: 'Pessoa Física', icon: User },
  { value: 'COMPANY', label: 'Pessoa Jurídica', icon: Building2 },
] as const;

export const ConvertToClientModal: React.FC<ConvertToClientModalProps> = ({ lead, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [clientType, setClientType] = useState<'PERSON' | 'COMPANY'>('PERSON');
  const [conversionNotes, setConversionNotes] = useState('');

  const handleConvert = async () => {
    try {
      setLoading(true);
      const result = await leadsService.convertToClient(lead.id, {
        clientType,
        additionalNotes: conversionNotes || undefined
      });
      if (result.success) onSuccess();
    } catch (error) {
      console.error('Erro ao converter lead:', error);
      toast.error(error instanceof Error ? getErrorMessage(error) : 'Erro ao converter lead');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && !loading && onClose()}>
      <DialogContent size="md" fullScreenOnMobile className="max-h-[90vh] overflow-y-auto max-sm:max-h-dvh">
        <DialogHeader>
          <DialogTitle>Converter em cliente</DialogTitle>
          <DialogDescription>Transformar o lead em cliente ativo.</DialogDescription>
        </DialogHeader>

        <div className="rounded-md border bg-muted/40 p-3 text-sm">
          <p className="font-medium">{lead.name}</p>
          <div className="mt-1 flex flex-col gap-0.5 text-muted-foreground">
            {lead.email && <span>Email: {lead.email}</span>}
            {lead.whatsapp && <span>WhatsApp: {lead.whatsapp}</span>}
            {lead.companyName && <span>Empresa: {lead.companyName}</span>}
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <p className="text-sm font-medium">Tipo de cliente *</p>
          <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Tipo de cliente">
            {CLIENT_TYPES.map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={clientType === value}
                onClick={() => setClientType(value)}
                disabled={loading}
                className={cn(
                  'flex flex-col items-center gap-1.5 rounded-lg border p-3 text-sm font-medium transition-colors',
                  clientType === value ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'hover:bg-muted'
                )}
              >
                <Icon className={cn('size-6', clientType === value ? 'text-primary' : 'text-muted-foreground')} />
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="conversion-notes">Notas de conversão (opcional)</Label>
          <Textarea
            id="conversion-notes"
            value={conversionNotes}
            onChange={(e) => setConversionNotes(e.target.value)}
            className="resize-none"
            rows={3}
            placeholder="Ex: Cliente aceitou proposta de R$ 50.000 para consultoria tributária..."
            disabled={loading}
          />
        </div>

        <div className="rounded-md border bg-muted/40 p-3 text-sm">
          <p className="font-medium">Esta ação irá:</p>
          <ul className="mt-1 list-inside list-disc text-muted-foreground">
            <li>Criar um novo cliente no sistema</li>
            <li>Migrar contatos e documentos do lead</li>
            <li>Marcar o lead como CONVERTIDO</li>
            <li>Criar evento na timeline</li>
          </ul>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>Cancelar</Button>
          <Button onClick={handleConvert} disabled={loading}>
            {loading && <Loader2 className="animate-spin" />}
            {loading ? 'Convertendo...' : 'Converter em Cliente'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConvertToClientModal;
